import { useState, useEffect, useContext, useCallback } from "react";
import { Stack, TextField } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import { Send28Filled } from "@fluentui/react-icons";

import styles from "./QuestionInput.module.css";
import { SpeechInput } from "./SpeechInput";
import { SessionAttachment } from "../../types/uploads";
import { LoginContext } from "../../loginContext";
import { requireLogin } from "../../authConfig";
import { configApi } from "../../api";
import { UploadFile } from "../UploadFile";
import { createPortal } from "react-dom";
import { Globe, Upload, Plus, ClipboardList, BarChart2, Brain, Workflow, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface UploadedFilesManager {
    files: SessionAttachment[];
    addFile: (file: SessionAttachment) => void;
    removeFile: (id: string) => void;
    clearFiles: () => void;
}

interface Props {
    onSend: (question: string, isWebSearch: boolean) => void | boolean | Promise<void>;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    showSpeechInput?: boolean;
    uploadedFilesManager?: UploadedFilesManager;
    onDocumentSearchToggle?: (enabled: boolean) => void;
    useDocumentSearch?: boolean;
    documentFiles?: string[];
    onDocumentFilesChange?: (files: string[]) => void;
    reportModeActive?: boolean;
    onReportModeToggle?: () => void;
    onReportModeDismiss?: () => void;
    analyticsModeActive?: boolean;
    onAnalyticsModeToggle?: () => void;
    onAnalyticsModeDismiss?: () => void;
    intelligenceModeActive?: boolean;
    onIntelligenceModeToggle?: () => void;
    onIntelligenceModeDismiss?: () => void;
    onWorkflowModeOpen?: () => void;
    allowSendWithoutText?: boolean;
    autoStartSpeechInputToken?: string | number | null;
    onSpeechInputAutoStarted?: () => void;
    externalUploadingFileName?: string;
}

export const QuestionInput = ({
    onSend,
    disabled,
    placeholder,
    clearOnSend,
    initQuestion,
    showSpeechInput,
    uploadedFilesManager,
    onDocumentSearchToggle,
    useDocumentSearch,
    onDocumentFilesChange,
    reportModeActive,
    onReportModeToggle,
    onReportModeDismiss,
    analyticsModeActive,
    onAnalyticsModeToggle,
    onAnalyticsModeDismiss,
    allowSendWithoutText,
    autoStartSpeechInputToken,
    onSpeechInputAutoStarted,
    externalUploadingFileName,
    intelligenceModeActive,
    onIntelligenceModeToggle,
    onIntelligenceModeDismiss,
    onWorkflowModeOpen
}: Props) => {
    const [question, setQuestion] = useState<string>("");
    const { loggedIn, isLoading } = useContext(LoginContext);
    const [isComposing, setIsComposing] = useState(false);
    const [voiceVisualizerContainer, setVoiceVisualizerContainer] = useState<HTMLDivElement | null>(null);

    const [showUserUpload, setShowUserUpload] = useState<boolean>(true);
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isWebSearch, setIsWebSearch] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [uploadingFileName, setUploadingFileName] = useState<string>("");
    const { t } = useTranslation();
    const handleVoiceVisualizerRef = useCallback((node: HTMLDivElement | null) => {
        setVoiceVisualizerContainer(node);
    }, []);

    useEffect(() => {
        const onDragOver = (e: DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer?.types.includes("Files")) {
                setIsDragOver(true);
            }
        };
        const onDrop = (e: DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            if (e.dataTransfer?.files?.length) {
                setDroppedFiles(Array.from(e.dataTransfer.files));
            }
        };
        const onDragLeave = (e: DragEvent) => {
            if (e.relatedTarget === null) {
                setIsDragOver(false);
            }
        };

        window.addEventListener("dragover", onDragOver);
        window.addEventListener("drop", onDrop);
        window.addEventListener("dragleave", onDragLeave);

        return () => {
            window.removeEventListener("dragover", onDragOver);
            window.removeEventListener("drop", onDrop);
            window.removeEventListener("dragleave", onDragLeave);
        };
    }, []);

    // Add clipboard paste support for images and files
    useEffect(() => {
        const onPaste = (e: ClipboardEvent) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            const files: File[] = [];

            // Extract files from clipboard
            for (let i = 0; i < items.length; i++) {
                const item = items[i];

                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) {
                        // Rename pasted images with timestamp for better identification
                        if (file.type.startsWith('image/') && file.name === 'image.png') {
                            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                            const renamedFile = new File(
                                [file],
                                `pasted-image-${timestamp}.png`,
                                { type: file.type }
                            );
                            files.push(renamedFile);
                        } else {
                            files.push(file);
                        }
                    }
                }
            }

            // If files found, prevent default paste and add to upload queue
            if (files.length > 0) {
                e.preventDefault();
                setDroppedFiles(prevFiles => [...prevFiles, ...files]);
            }
        };

        window.addEventListener('paste', onPaste);

        return () => {
            window.removeEventListener('paste', onPaste);
        };
    }, []);

    useEffect(() => {
        const handleProjectFilesDropReset = () => setIsDragOver(false);
        window.addEventListener("project-files-drop-reset", handleProjectFilesDropReset);
        return () => window.removeEventListener("project-files-drop-reset", handleProjectFilesDropReset);
    }, []);

    const getConfig = async () => {
        configApi().then(config => {
            setShowUserUpload(config.showUserUpload);
        });
    };

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    useEffect(() => {
        getConfig();
    }, []);

    const hasAttachedFiles = !!uploadedFilesManager && uploadedFilesManager.files.length > 0;

    const sendQuestion = () => {
        if (disabled) {
            return;
        }
        if (!question.trim() && !(allowSendWithoutText && hasAttachedFiles)) {
            return;
        }

        const result = onSend(question, isWebSearch);

        if (clearOnSend && result !== false) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (isComposing) return;

        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const handleCompositionStart = () => {
        setIsComposing(true);
    };
    const handleCompositionEnd = () => {
        setIsComposing(false);
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (!newValue) {
            setQuestion("");
        } else {
            // Always show the text, even if it exceeds the limit (like ChatGPT)
            setQuestion(newValue);
        }
    };

    const maxLength = 50000;
    const disableRequiredAccessControl = requireLogin && !loggedIn && !isLoading;
    const exceedsLimit = question.length > maxLength;
    const hasSendableContent = !!question.trim() || (!!allowSendWithoutText && hasAttachedFiles);
    const sendQuestionDisabled = disabled || !hasSendableContent || disableRequiredAccessControl || exceedsLimit;

    if (disableRequiredAccessControl) {
        placeholder = "Please login to continue...";
    }

    const handleUploadStart = (filename: string) => {
        setIsUploading(true);
        setUploadingFileName(filename);
    };

    const handleUploadEnd = () => {
        setIsUploading(false);
        setUploadingFileName("");
    };

    const showUploadProgress = isUploading || !!externalUploadingFileName;
    const uploadProgressFileName = isUploading ? uploadingFileName : externalUploadingFileName || "";

    return (
        <Stack horizontal className={`${styles.questionInputContainer} ${isDragOver ? styles.dragOver : ""} ${showUploadProgress ? styles.uploading : ""} ${exceedsLimit ? styles.exceedsLimit : ""}`}>
            {isDragOver &&
                createPortal(
                    <div className={`${styles.dragDropOverlay} ${isDragOver ? styles.active : ""}`} onDragOver={e => e.preventDefault()}>
                        <div className={styles.dragDropContent}>
                            <Upload size={48} />
                            <p>{t("dropFiles")}</p>
                        </div>
                    </div>,
                    document.getElementById("drag-drop-root")!
                )}
            {showUploadProgress && (
                <div className={styles.uploadProgress}>
                    <div className={styles.uploadProgressContent}>
                        <div className={styles.uploadProgressText}>
                            <div className={styles.uploadSpinner}></div>
                            {t("uploadingFile", { fileName: uploadProgressFileName }) || `Uploading ${uploadProgressFileName}...`}
                        </div>
                        <div className={styles.uploadProgressBar}>
                            <div className={styles.uploadProgressFill}></div>
                        </div>
                    </div>
                </div>
            )}
            <TextField
                className={styles.questionInputTextArea}
                disabled={disableRequiredAccessControl}
                placeholder={placeholder}
                styles={{
                    fieldGroup: {
                        minHeight: 40
                    }
                }}
                multiline
                resizable={false}
                borderless
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onEnterPress}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                autoAdjustHeight={true}
            />
            {(question.length > maxLength * 0.8 || exceedsLimit) && (
                <div className={`${styles.characterCounter} ${exceedsLimit ? styles.exceedsLimit : ''}`}>
                    {question.length.toLocaleString()}/{maxLength.toLocaleString()}
                    {exceedsLimit && <span className={styles.limitWarning}> - Too long to send</span>}
                </div>
            )}
            <div className={styles.questionInputButtonsContainer}>
                <div className={styles.uploadSearchContainer}>
                    <button
                        className={`${styles.plusButton} ${isMenuOpen ? styles.plusButtonExpanded : ""}`}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        type="button"
                        aria-label="Toggle menu"
                    >
                        <Plus size={20} />
                    </button>
                    {isMenuOpen && (
                        <>
                            {showUserUpload && <UploadFile
                                externalFiles={droppedFiles}
                                className={styles.uploadButton}
                                disabled={!loggedIn}
                                onFileUploaded={uploadedFilesManager?.addFile}
                                onExternalFilesProcessed={() => setDroppedFiles([])}
                                onUploadStart={handleUploadStart}
                                onUploadEnd={handleUploadEnd}
                                onDocumentSearchToggle={onDocumentSearchToggle}
                                useDocumentSearch={useDocumentSearch}
                                onDocumentFilesChange={onDocumentFilesChange}
                            />}
                            <div
                                className={`${styles.webSearchDiv} ${isWebSearch ? styles.activeWebSearchDiv : ""}`}
                                onClick={() => setIsWebSearch(!isWebSearch)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => e.key === "Enter" && setIsWebSearch(!isWebSearch)}
                            >
                                <Globe size={20} />
                                <span>{t("search")}</span>
                            </div>
                            {onReportModeToggle && (
                                <div
                                    className={`${styles.webSearchDiv} ${reportModeActive ? styles.activeWebSearchDiv : ""}`}
                                    onClick={() => { onReportModeToggle(); setIsMenuOpen(false); }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => { if (e.key === "Enter") { onReportModeToggle(); setIsMenuOpen(false); } }}
                                >
                                    <ClipboardList size={20} />
                                    <span>{t("reportMode.menuLabel")}</span>
                                </div>
                            )}
                            {onAnalyticsModeToggle && (
                                <div
                                    className={`${styles.webSearchDiv} ${analyticsModeActive ? styles.activeWebSearchDiv : ""}`}
                                    onClick={() => { onAnalyticsModeToggle(); setIsMenuOpen(false); }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => { if (e.key === "Enter") { onAnalyticsModeToggle(); setIsMenuOpen(false); } }}
                                >
                                    <BarChart2 size={20} />
                                    <span>{analyticsModeActive ? t("projectPage.analytics.toggleOffLabel") : t("projectPage.analytics.toggleLabel")}</span>
                                </div>
                            )}
                            {onIntelligenceModeToggle && (
                                <div
                                    className={`${styles.webSearchDiv} ${intelligenceModeActive ? styles.activeWebSearchDiv : ""}`}
                                    onClick={() => {
                                        if (!intelligenceModeActive) {
                                            setIsWebSearch(false);
                                        }
                                        onIntelligenceModeToggle();
                                        setIsMenuOpen(false);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            if (!intelligenceModeActive) {
                                                setIsWebSearch(false);
                                            }
                                            onIntelligenceModeToggle();
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    <Brain size={20} />
                                    <span>{t("homeChat.intelligence.toggleLabel")}</span>
                                </div>
                            )}
                            {onWorkflowModeOpen && (
                                <button
                                    type="button"
                                    className={styles.webSearchDiv}
                                    disabled={disabled || disableRequiredAccessControl}
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        onWorkflowModeOpen();
                                    }}
                                >
                                    <Workflow size={20} aria-hidden="true" />
                                    <span>{t("workflowBuilder.entry", { defaultValue: "Workflow" })}</span>
                                </button>
                            )}
                        </>
                    )}
                    {reportModeActive && (
                        <div className={styles.reportModeBadge}>
                            <ClipboardList size={14} />
                            <span>{t("reportMode.badgeLabel")}</span>
                            {onReportModeDismiss && (
                                <button
                                    type="button"
                                    onClick={onReportModeDismiss}
                                    aria-label="Exit report mode"
                                    className={styles.reportModeBadgeClose}
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    )}
                    {analyticsModeActive && (
                        <div className={styles.analyticsModeBadge}>
                            <BarChart2 size={14} />
                            <span>{t("projectPage.analytics.toggleOffLabel")}</span>
                            {onAnalyticsModeDismiss && (
                                <button
                                    type="button"
                                    onClick={onAnalyticsModeDismiss}
                                    aria-label="Exit analytics mode"
                                    className={styles.analyticsModeBadgeClose}
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    )}
                    {intelligenceModeActive && (
                        <div className={styles.intelligenceModeBadge}>
                            <Brain size={14} />
                            <span>{t("homeChat.intelligence.toggleOffLabel")}</span>
                            {onIntelligenceModeDismiss && (
                                <button
                                    type="button"
                                    onClick={onIntelligenceModeDismiss}
                                    aria-label="Exit intelligence mode"
                                    className={styles.intelligenceModeBadgeClose}
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.questionInputActions}>
                    {showSpeechInput && (
                        <SpeechInput
                            updateQuestion={setQuestion}
                            visualizerContainer={voiceVisualizerContainer}
                            autoStartToken={autoStartSpeechInputToken}
                            onAutoStartHandled={onSpeechInputAutoStarted}
                        />
                    )}
                    <Button size="large" icon={<Send28Filled primaryFill="rgba(97, 85, 245, 1)" />} disabled={sendQuestionDisabled} onClick={sendQuestion} />
                </div>
            </div>
            {showSpeechInput && <div className={styles.voiceVisualizerSlot} ref={handleVoiceVisualizerRef} />}
        </Stack>
    );
};
